/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import React, { useEffect } from 'react';

import { UpcomingOffer, UpcomingOfferV2 } from 'client-code/upcoming-offers';

import { BrandingUiInfoKeys } from '../../../core/entities';
import { ServicesClientImpl } from '../../../core/interactors';
import { RootState, useTivoSelector } from '../../../core/tivo/tivo-redux-wrapper';
import { DateTime } from '../../../core/utils';
import { Mousetrap } from '../../common';
import { RecordingStatusIcon } from '../../Guide/RecordingStatusIcon';

import './UpcomingList.scss';
import { Locale, LocaleKeys } from '../../../core/locale';

interface UpcomingListProps {
    items: UpcomingOffer[] | any;
    selectedIndex?: number;
    ghostMode?: boolean;
}

const UpcomingList: React.FC<UpcomingListProps> = ({ items, selectedIndex, ghostMode }) => {
    const services = new ServicesClientImpl();
    const newIcon = services.getAppUiImageByKey(BrandingUiInfoKeys.APP_ICONS.NEW_ICON);
    const { recordingsStatusMap } = useTivoSelector((state: RootState) => state.recordingsStatus);
    const [upcomingItems, setUpcomingItems] = React.useState<any>([]);

    const formatDate = (startTime: string): string => {
        const dateTime = new DateTime(startTime);
        return dateTime ? dateTime.strftime('%M/%D') : '';
    };

    const formatTime = (startTime: string): string => {
        const dateTime = new DateTime(startTime);
        return dateTime ? dateTime.strftime('%l:%i%p') : '';
    };

    const getRecordingStatusIcon = async (item: UpcomingOffer): Promise<string> => {
        const getChannelData = await services.getChannelFromChannelNumber(
            item.channel.channelNumber,
        );

        const { offerId } = item;
        const recordingStatus = recordingsStatusMap.get(`${getChannelData.stationId}${offerId}`);
        return recordingStatus || '';
    };

    const getResolutionIcon = (resolution: string): string => {
        switch (resolution.toLowerCase()) {
            case 'hd':
                return 'HD';
            case 'sd':
                return 'SD';
            case '4k':
            case 'uhd':
                return '4K';
            default:
                return '';
        }
    };

    const formatEpisodeTitle = (item: UpcomingOffer | UpcomingOfferV2): string => {
        const { seasonOrYear, episodeNum, title } = item as any;

        if (seasonOrYear && episodeNum) {
            return `S${seasonOrYear} E${episodeNum} '${title}'`;
        }
        if (seasonOrYear) {
            return `S${seasonOrYear} '${title}'`;
        }

        return title || Locale.getString(LocaleKeys.TITLE_NOT_AVAILABLE);
    };

    useEffect(() => {
        const updateItemsWithRecordingStatus = async () => {
            const updatedItems = await Promise.all(
                items.map(async (item: UpcomingOffer | UpcomingOfferV2) => {
                    const recordingStatus = await getRecordingStatusIcon(item);
                    // If recording status is present, append it to the item, else ignore
                    if (recordingStatus) {
                        return { ...item, recordingStatus };
                    }
                    return item;
                }),
            );

            setUpcomingItems(updatedItems);
        };

        updateItemsWithRecordingStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items]);

    return (
        <div className="upcoming-list">
            <div className="upcoming-list-content">
                {upcomingItems?.map((item, index) => (
                    <Mousetrap
                        value={`${index}_list-view`}
                        key={`${item.id}-${index}`}
                        className={`upcoming-list-item ${index === selectedIndex ? ` focused${ghostMode ? ' ghostHighlight' : ''}` : ''}`}
                    >
                        <div className="upcoming-list-item-content">
                            <div className="item-date">{formatDate(item.startTime)}</div>

                            <div className="item-time">{formatTime(item.startTime)}</div>

                            <div className="item-content">
                                <span className="item-title">{formatEpisodeTitle(item)}</span>
                                {item.isNew && (
                                    <img
                                        src={newIcon?.imageUrl}
                                        className="icon-new"
                                        data-testid={'program-cell-new-icon'}
                                    />
                                )}
                            </div>

                            <div className="item-channel-info">
                                <span className="channel-number">{item.channel.channelNumber}</span>
                                {getResolutionIcon(item.videoResolution || '') && (
                                    <span className="resolution-badge">
                                        {item.videoResolution && (
                                            <span>
                                                {getResolutionIcon(item.videoResolution || '')}
                                            </span>
                                        )}
                                    </span>
                                )}
                                <div className="additional-icons">
                                    {item.recordingStatus && (
                                        <RecordingStatusIcon type={item.recordingStatus} />
                                    )}
                                </div>
                            </div>
                        </div>
                    </Mousetrap>
                ))}
            </div>
        </div>
    );
};

export default UpcomingList;
